import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Language {
  code: string
  name: string
  native: string
  status: string
  strings: number
  translated: number
}

export default function LocalizationPage() {
  const [languages, setLanguages] = useState<Language[]>([])
  const [strings, setStrings] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  useEffect(() => {
    fetchData()
  }, [selectedCategory])

  const fetchData = async () => {
    const catParam = selectedCategory ? `?category=${selectedCategory}` : ''
    const [langRes, strRes, catRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/localization/languages'),
      fetch(API_BASE + '/localization/strings' + catParam),
      fetch(API_BASE + '/localization/categories'),
      fetch(API_BASE + '/localization/statistics')
    ])
    if (langRes.ok) setLanguages((await langRes.json()).languages || [])
    if (strRes.ok) setStrings((await strRes.json()).strings || [])
    if (catRes.ok) setCategories((await catRes.json()).categories || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete': return 'bg-green-100 text-green-800'
      case 'in_progress': return 'bg-yellow-100 text-yellow-800'
      case 'pending': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getProgress = (lang: Language) => {
    return (lang.translated / lang.strings * 100).toFixed(1)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Localization Manager</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Language</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Languages</div>
              <div className="text-2xl font-bold">{statistics.total_languages}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Complete</div>
              <div className="text-2xl font-bold text-green-600">{statistics.complete_languages}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Strings</div>
              <div className="text-2xl font-bold">{statistics.total_strings}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Categories</div>
              <div className="text-2xl font-bold">{statistics.categories}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Default</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.default_language?.toUpperCase()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Coverage</div>
              <div className="text-2xl font-bold text-green-600">{statistics.translation_coverage}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold mb-4">Categories</h3>
            <div className="space-y-2">
              <button onClick={() => setSelectedCategory(null)} className={`w-full text-left px-3 py-2 rounded ${!selectedCategory ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`}>All</button>
              {categories.map(cat => (
                <button key={cat.name} onClick={() => setSelectedCategory(cat.name)} className={`w-full text-left px-3 py-2 rounded flex justify-between ${selectedCategory === cat.name ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`}>
                  <span className="capitalize">{cat.name}</span>
                  <span className="text-xs bg-gray-200 px-2 py-1 rounded">{cat.strings}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="col-span-3 space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Languages</h2></div>
              <div className="divide-y">
                {languages.map(lang => (
                  <div key={lang.code} className="p-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold">{lang.native}</span>
                        <span className="text-gray-500">({lang.name})</span>
                        <span className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">{lang.code}</span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(lang.status)}`}>{lang.status}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${lang.status === 'complete' ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${getProgress(lang)}%` }}></div>
                      </div>
                      <span className="text-sm text-gray-500">{lang.translated}/{lang.strings} ({getProgress(lang)}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Strings</h2></div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Key</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">EN</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">DE</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">FR</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {strings.map(str => (
                    <tr key={str.key}>
                      <td className="px-6 py-4 font-mono text-sm">{str.key}</td>
                      <td className="px-6 py-4"><span className="px-2 py-1 bg-gray-100 rounded text-xs capitalize">{str.category}</span></td>
                      <td className="px-6 py-4 text-sm">{str.en}</td>
                      <td className="px-6 py-4 text-sm">{str.de || <span className="text-gray-400">-</span>}</td>
                      <td className="px-6 py-4 text-sm">{str.fr || <span className="text-gray-400">-</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
